import { flattenDeck } from "@hali-shares/deck-schema";
import { useState } from "react";
import { SlidePreview } from "../../../deck/SlidePreview";
import {
  SLIDE_KINDS,
  blockMeta,
  blockText,
  chipOf,
  citedRefs,
  newBlock,
  referenceMeta,
  sectionStatus,
  type SectionStatus,
} from "../model";
import type { StudioCtx } from "./ctx";

const THUMB_W = 224;

const STATUS_TEXT: Record<SectionStatus, string> = { empty: "To draft", drafted: "Drafted", reviewed: "Reviewed" };

/**
 * Step 3: one section at a time. An empty section offers an AI draft from its plan (or a slide to start by hand);
 * a written one shows its slides as the room will see them, each opening its editor. "Mark reviewed" moves on.
 */
export function SlidesStep({ ctx }: { ctx: StudioCtx }) {
  const { deck, settings, selected } = ctx;
  const si = Math.min(selected, deck.sections.length - 1);
  const section = deck.sections[si]!;
  const status = sectionStatus(section, settings.plan[section.id]);
  const intent = settings.plan[section.id]?.intent;
  const aiOn = ctx.ai?.configured !== false;
  const busy = ctx.busy === `section:${section.id}`;
  const error = ctx.aiError?.where === `section:${section.id}` ? ctx.aiError : null;
  const [ask, setAsk] = useState("");
  const last = si === deck.sections.length - 1;

  const slides = flattenDeck(deck);
  const first = slides.findIndex((s) => s.kind === "section" && s.sectionId === section.id);
  const refs = citedRefs(section)
    .map((id) => deck.references.find((r) => r.id === id))
    .filter((r): r is NonNullable<typeof r> => Boolean(r));

  const draft = async () => {
    if (await ctx.draftSection(si, ask)) setAsk("");
  };
  const markReviewed = () => {
    ctx.saveSettings({ plan: { ...settings.plan, [section.id]: { ...settings.plan[section.id], reviewed: true } } });
    if (last) ctx.goto("review");
    else ctx.setSelected(si + 1);
    window.scrollTo({ top: 0 });
  };
  const add = (type: (typeof SLIDE_KINDS)[number]["type"]) =>
    type === "reference" ? ctx.openReference(null) : ctx.openBlock(si, null, newBlock(type, deck));

  return (
    <div className="slides-step">
      <nav className="section-rail" aria-label="Sections">
        <p className="col-label">Sections</p>
        <ol>
          {deck.sections.map((s, i) => {
            const st = sectionStatus(s, settings.plan[s.id]);
            return (
              <li key={s.id}>
                <button type="button" className="rail-row" aria-current={i === si} onClick={() => ctx.setSelected(i)}>
                  <span className="o-num">{String(i + 1).padStart(2, "0")}</span>
                  <span className="rail-main">
                    <span className="rail-title">{s.title}</span>
                    <span className="rail-status" data-status={st}>
                      <StatusMark status={st} /> {STATUS_TEXT[st]} · {s.minutes}m
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
        <button type="button" className="link-btn" onClick={() => ctx.goto("outline")}>
          Change the outline
        </button>
      </nav>

      <div className="sec-main">
        <header className="section-head">
          <p className="step-eyebrow">
            Section {si + 1} of {deck.sections.length} · {section.minutes} min · {STATUS_TEXT[status]}
          </p>
          <div className="section-head-row">
            <h1 className="step-h">{section.title}</h1>
            <button type="button" className="s-btn s-btn-small" onClick={() => ctx.openSection(si)}>
              Edit section
            </button>
          </div>
          {intent ? (
            <p className="step-lead">{intent}</p>
          ) : (
            <p className="step-lead step-lead-muted">
              No purpose written for this section. Adding one in the outline gives the AI a clearer brief.
            </p>
          )}
          {section.ahead.length > 0 && (
            <ul className="points" aria-label="Key points">
              {section.ahead.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          )}
        </header>

        {status === "empty" ? (
          <section className="step-card ai-card" aria-labelledby="draft-h">
            <h2 id="draft-h" className="step-card-h">
              Draft this section with AI
            </h2>
            <p className="step-card-lead">
              From its purpose and key points, your brief and your notes. You can change, redraft or delete every slide
              afterwards.
            </p>
            <div className="row-gap">
              <input
                className="input"
                aria-label="Anything to include?"
                placeholder="Anything to include? e.g. a quiz on retries, the latency numbers (optional)"
                value={ask}
                maxLength={1000}
                disabled={!aiOn}
                onChange={(e) => setAsk(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && !busy && aiOn && void draft()}
              />
              <button
                type="button"
                className="s-btn s-btn-ink s-btn-tall"
                disabled={busy || ctx.busy !== null || !aiOn}
                onClick={() => void draft()}
              >
                {busy ? "Drafting…" : "Draft slides"}
              </button>
            </div>
            {busy && <span className="busy">WRITING THIS SECTION · ABOUT HALF A MINUTE</span>}
            {!aiOn && (
              <p className="banner banner-grey">
                AI is off: set OPENAI_API_KEY for the server. Add slides yourself below.
              </p>
            )}
            {error && <AiErrorBanner error={error} />}
          </section>
        ) : (
          <>
            <section aria-label="Slides" className="slide-cards">
              {section.blocks.map((b, i) => (
                <button key={b.id} type="button" className="slide-card" onClick={() => ctx.openBlock(si, i, b)}>
                  <SlidePreview deck={deck} slideIndex={first + 1 + i} width={THUMB_W} />
                  <span className="slide-card-text">
                    <span className={`chip chip-${chipOf(b.type)}`}>{chipOf(b.type)}</span>
                    <span className="b-text">{blockText(b)}</span>
                    <span className="b-meta">{blockMeta(b, deck)}</span>
                  </span>
                </button>
              ))}
            </section>
            {refs.length > 0 && (
              <div className="blocks" aria-label="References cited here">
                {refs.map((r) => (
                  <button key={r.id} type="button" className="b-row" onClick={() => ctx.openReference(r.id)}>
                    <span className="chip chip-REFERENCE">REFERENCE</span>
                    <span className="b-main">
                      <span className="b-text">{r.title}</span>
                      <span className="b-meta">{referenceMeta(r)}</span>
                    </span>
                  </button>
                ))}
              </div>
            )}
            <section className="step-card ai-card refine" aria-labelledby="refine-h">
              <label id="refine-h" htmlFor="refine" className="step-card-h">
                Change this section
              </label>
              <div className="row-gap">
                <input
                  id="refine"
                  className="input"
                  type="text"
                  value={ask}
                  maxLength={1000}
                  disabled={!aiOn}
                  placeholder="Say what to change, e.g. shorter, one idea per slide, end with a quiz"
                  onChange={(e) => setAsk(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && !busy && aiOn && void draft()}
                />
                <button
                  type="button"
                  className="s-btn s-btn-ink s-btn-tall"
                  disabled={busy || ctx.busy !== null || !aiOn}
                  onClick={() => void draft()}
                >
                  {busy ? "Redrafting…" : "Redraft section"}
                </button>
              </div>
              {busy && <span className="busy">REWRITING THIS SECTION · ABOUT HALF A MINUTE</span>}
              {error && <AiErrorBanner error={error} />}
            </section>
          </>
        )}

        <section className="step-card" aria-labelledby="add-h">
          <h2 id="add-h" className="step-card-h">
            {status === "empty" ? "Or add a slide yourself" : "Add a slide"}
          </h2>
          <div className="kind-grid">
            {SLIDE_KINDS.map((k) => (
              <button key={k.type} type="button" className="kind-card" onClick={() => add(k.type)}>
                <span className="kind-label">+ {k.label}</span>
                <span className="kind-use">{k.use}</span>
              </button>
            ))}
          </div>
        </section>

        <footer className="step-foot">
          <button
            type="button"
            className="s-btn s-btn-tall"
            disabled={si === 0}
            onClick={() => ctx.setSelected(si - 1)}
          >
            Previous section
          </button>
          <span className="spacer" />
          {status === "reviewed" ? (
            <button
              type="button"
              className="s-btn s-btn-ink s-btn-tall"
              onClick={() => (last ? ctx.goto("review") : ctx.setSelected(si + 1))}
            >
              {last ? "Next: review the talk" : "Next section"}
            </button>
          ) : (
            <button
              type="button"
              className="s-btn s-btn-ink s-btn-tall"
              disabled={status === "empty"}
              title={status === "empty" ? "Draft or add slides first" : undefined}
              onClick={markReviewed}
            >
              {last ? "Mark reviewed, then review the talk" : "Mark reviewed, next section"}
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}

function StatusMark({ status }: { status: SectionStatus }) {
  if (status === "reviewed")
    return (
      <svg
        aria-hidden="true"
        width="10"
        height="10"
        viewBox="0 0 12 12"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M2.5 6.2 L5 8.5 L9.5 3.5" />
      </svg>
    );
  return <span aria-hidden="true" className="status-dot" data-status={status} />;
}

export function AiErrorBanner({ error }: { error: { message: string; issues: { path: string; message: string }[] } }) {
  return (
    <div className="banner banner-yellow" role="alert">
      {error.message}
      {error.issues.length > 0 && (
        <ul className="issues">
          {error.issues.slice(0, 6).map((i, n) => (
            <li key={n}>
              {i.path}: {i.message}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
